import { trim } from 'lodash';
import range from 'lodash/range';
import httpClient from '@common/api/httpClient';
import {
  ContentRestriction,
  type ContractsResponse,
  type DeviceSnapshotRequestType,
  type InstallationComment,
  type InstallationFull,
  type InstallationInfo,
  type InstallationInfoByMacResponse,
  type InstallationProfile,
  type InstallationsInfoQuery,
  type PaginatedItemsResponse,
} from '@common/types/api';
import { type DeviceId, type InstallationId } from '@common/types/brandedTypes';
import type Contract from '@common/types/models/Contract';
import {
  type ContractType,
  createContractFromResponse,
  noTypeContractTypeId,
} from '@common/types/models/Contract';
import {
  createDeviceAccessRequestFromResponse,
  type DeviceAccessRequest,
} from '@common/types/models/Device/DeviceAccessRequest';
import type DeviceFull from '@common/types/models/Device/DeviceFull';
import {
  createDeviceFullFromResponse,
  createDeviceUpdateRequest,
} from '@common/types/models/Device/DeviceFull';
import type DeviceShort from '@common/types/models/Device/DeviceShort';
import { createDeviceShortFromResponse } from '@common/types/models/Device/DeviceShort';
import type DeviceType from '@common/types/models/DeviceType';
import { createDeviceTypeFromResponse } from '@common/types/models/DeviceType';
import { type ListWithCount } from '@common/utils/formatting/paginationQueryHelpers';

export function updateDeviceAddress(deviceId: InstallationId, address: DeviceFull['location']) {
  return httpClient.put(
    `/rest/v1/Installations/${deviceId}`,
    { address },
  );
}

export function saveDevice(device: DeviceFull) {
  return httpClient.put(
    `/rest/v1/Installations/${device.id}`,
    createDeviceUpdateRequest({
      ...device,
      name: trim(device.name),
    }),
  );
}

export async function fetchDeviceByInstallationId(
  installationId: InstallationId,
): Promise<DeviceFull> {
  const { data } = await httpClient.get<InstallationFull>(
    `/rest/v1/Installations/${installationId}`,
  );
  return createDeviceFullFromResponse(data);
}

export async function fetchDevices(
  query: InstallationsInfoQuery,
  signal?: AbortSignal,
): Promise<ListWithCount<DeviceShort | DeviceAccessRequest>> {
  const { data } = await httpClient.post<PaginatedItemsResponse<InstallationInfo>>(
    '/rest/v1/InstallationsInfo',
    query,
    { signal },
  );

  const items = data?.items?.map((item) => ((item.contentRestriction === ContentRestriction.None)
    ? createDeviceShortFromResponse(item)
    : createDeviceAccessRequestFromResponse(item))
  ) ?? [];
  return {
    items,
    count: data?.totalCount ?? 0,
  };
}

export async function fetchInstallationIds(
  query: InstallationsInfoQuery,
): Promise<PaginatedItemsResponse<InstallationId>> {
  const limit = 5000;
  const fetchPiece = async (offset = 0) => {
    const { data } = await httpClient.post<PaginatedItemsResponse<InstallationId>>(
      '/rest/v1/InstallationsInfo/id',
      { ...query, offset, limit },
    );
    return data;
  };

  const firstResponse = await fetchPiece();
  const totalCount = firstResponse.totalCount;

  const remainingResponses = await Promise.all(
    range(firstResponse.items.length, totalCount, limit)
      .map((offset) => fetchPiece(offset)),
  );

  return {
    items: [firstResponse, ...remainingResponses].flatMap((piece) => piece.items),
    totalCount,
  };
}

export async function fetchDeviceComment(
  installationId: InstallationId,
  signal: AbortSignal,
): Promise<string | undefined> {
  try {
    const { data } = await httpClient.get<InstallationComment>(
      `/rest/v1/Installation/${installationId}/Comment`,
      { signal },
    );
    return data?.comment ?? '';
  } catch {
    return undefined;
  }
}

export async function updateDeviceComment(
  installationId: InstallationId,
  comment: string,
  oldComment?: string,
): Promise<void> {
  const url = `/rest/v1/Installation/${installationId}/Comment`;
  if (comment.length === 0) {
    await httpClient.delete(url);
    return;
  }
  const body = { comment };
  if (!oldComment) {
    await httpClient.post(url, body);
    return;
  }
  await httpClient.put(url, body);
}

export async function fetchAllDeviceTypes(): Promise<DeviceType[] | undefined> {
  const { data } = await httpClient.get<InstallationProfile[]>(
    '/rest/v1/InstallationProfiles?onlyWithInstallations=true',
  );
  return data?.map((item) => createDeviceTypeFromResponse(item));
}

export async function fetchInstallationProfile(
  installationProfileId: string,
  signal: AbortSignal,
): Promise<DeviceType | undefined> {
  const { data } = await httpClient.get<InstallationProfile>(
    `/rest/v1/InstallationProfiles/${installationProfileId}`,
    { signal },
  );
  return data ? createDeviceTypeFromResponse(data) : undefined;
}

export async function fetchDeviceContract(
  installationId: InstallationId,
  signal: AbortSignal,
): Promise<Contract | undefined> {
  try {
    const { data: [contract] } = await httpClient.get<ContractsResponse[]>(
      `/rest/v1/installation/${installationId}/contracts`,
      { signal },
    );
    if (contract) {
      return createContractFromResponse(contract);
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export function updateDeviceContractType(
  installationId: InstallationId,
  contractType: number,
): Promise<void> {
  if (contractType !== noTypeContractTypeId) {
    return httpClient.put(
      `/rest/v1/InstallationContractType/${installationId}/contractType/${contractType}`,
    );
  }
  return httpClient.delete(
    `/rest/v1/InstallationContractType/${installationId}/contractType`,
  );
}

export async function fetchContractTypes(): Promise<ContractType[]> {
  const { data } = await httpClient.get<ContractType[]>('/rest/v1/ContractTypes');
  // Names have [[ ]] around them
  return data.map((item: ContractType) => ({
    id: item.id,
    name: item.name.replace('[[', '').replace(']]', ''),
  }));
}

export async function getDeviceByMac(
  macAddress: string,
) {
  const { data } = await httpClient.get<InstallationInfoByMacResponse>(
    `/rest/v1/Installations/Mac/${encodeURIComponent(macAddress)}`,
  );
  return {
    ...data,
    name: trim(data.name),
  };
}

export async function deleteDeviceFromCloud(
  deviceId: DeviceId,
  devicePid: number,
  force = false,
) {
  return httpClient.delete(`/rest/v1/Devices/${deviceId}?pid=${devicePid}&force=${force}`);
}

export async function restoreDeviceInCloud(
  deviceId: DeviceId,
) {
  return httpClient.post(`/rest/v1/Devices/${deviceId}/CancelDelete`);
}

export async function getDeviceSnapshot(
  deviceId: DeviceId,
  requestType: DeviceSnapshotRequestType,
  signal?: AbortSignal,
) {
  return httpClient.post(
    `/rest/v1/DeviceSnapshotRequests`,
    { deviceId, type: requestType },
    {
      signal,
    },
  );
}

export async function unregisterDevice(
  installationId: InstallationId,
  token: string,
) {
  await httpClient.post(`/rest/v1/Unregister/Installation/${installationId}/${token}`);
}
